import React, { useState } from 'react';
import config from '@config';
import { BOTTOM_INDENTS, COLORS } from '@constants';
import { ACCOUNT_STATUS, PLATFORM_NAME } from '@enums';
import { useStore } from '@hooks';
import { Button, Divider, Icon, Loader, Typography, WhiteCover } from '@ui';
import { formatString } from '@utils';
import { Spin } from 'antd';
import { observer } from 'mobx-react';
import { SETUP_KEYS } from '../../constants';
import SubdomainCollapse from '../../modals/SubdomainCollapse';
import LineItem from '../../shared/LineItem';
import CoverTitle from '../CoverTitle';
import { StyledCollapseContent } from '../StoreSetupManual/styled';
import Connected from './Connected/index';
import NotConnected from './NotConnected/index';

const IntegrationBig = observer(({ storeUrl, isValid = false, setModal, modal, onModalClose }) => {
    const store = useStore();
    const { getEcommerceRedirect, isFormPending } = store.authStore;
    const { companyName, setupInfo, currentAccount } = store.accountsStore;

    const connected = currentAccount.status !== ACCOUNT_STATUS.DISCONNECTED;

    const [analyticsScriptOpen, setAnalyticsScriptOpen] = useState(false);
    const [purchaseEventOpen, setPurchaseEventOpen] = useState(false);

    const updateScopes = () => {
        getEcommerceRedirect(PLATFORM_NAME.SHOPIFY, {
            companyName,
            shop: storeUrl,
            token_type: 'offline'
        });
    };

    const getIsOpen = (key) => {
        if (key === SETUP_KEYS.ANALYTICS_SCRIPT) return analyticsScriptOpen;
        if (key === SETUP_KEYS.PURCHASE_EVENT) return purchaseEventOpen;
        return false;
    };

    const getToggleCollapse = (key) => {
        if (key === SETUP_KEYS.ANALYTICS_SCRIPT) return () => setAnalyticsScriptOpen(!analyticsScriptOpen);
        if (key === SETUP_KEYS.PURCHASE_EVENT) return () => setPurchaseEventOpen(!purchaseEventOpen);
        return () => {};
    };

    const setupConfig = {
        [SETUP_KEYS.ANALYTICS_SCRIPT]: {
            title: 'Analytics script setup',
            status: setupInfo.scriptTag.status,
            isManual: false
        },
        [SETUP_KEYS.PURCHASE_EVENT]: {
            title: 'Purchase events setup',
            status: setupInfo.purchaseEventStatus,
            isManual: false
        }
    };

    return (
        <Spin spinning={isFormPending} indicator={<Loader />}>
            <WhiteCover
                data-e2e='integrations_bigcommerce_section'
                customTitle={<CoverTitle title='Big Commerce' icon={<Icon.BigCommerceIcon />} />}
                style={BOTTOM_INDENTS.M}
                rightContent={
                    <Button
                        data-e2e='integrations_go-to-bigcommerce_btn'
                        type='link'
                        padding='0'
                        onClick={() => {
                            window.open(
                                `https://store-${currentAccount.externalId}.mybigcommerce.com/manage`,
                                '_blank'
                            );
                        }}
                    >
                        Go to BigCommerce
                    </Button>
                }
            >
                <Typography.Text
                    data-e2e='integrations_bigcommerce_description'
                    type='body2'
                    color={COLORS.$gray60Black}
                    style={BOTTOM_INDENTS.M}
                >
                    Sync BigCommerce to manage inventory, product details and fulfill orders all from BigCommerce.
                </Typography.Text>
                {connected ? (
                    <Connected
                        setupInfo={setupInfo}
                        isValid={isValid}
                        trimmedDomain={`store-${currentAccount.externalId}`}
                        updateScopes={updateScopes}
                    />
                ) : (
                    <NotConnected
                        url={`https://store-${currentAccount.externalId}.mybigcommerce.com/manage/marketplace/apps/${config.bcAppId}`}
                    />
                )}

                {Object.entries(setupConfig).map(([key, data]) => {
                    const isAnalyticsScript = key === SETUP_KEYS.ANALYTICS_SCRIPT;
                    const isPurchaseEvent = key === SETUP_KEYS.PURCHASE_EVENT;
                    const isOpen = getIsOpen(key);
                    const toggleCollapse = getToggleCollapse(key);

                    return (
                        <div
                            key={key}
                            style={BOTTOM_INDENTS.S}
                            data-e2e={`integrations_${formatString(data.title)}_block`}
                        >
                            <LineItem
                                setModal={setModal}
                                modal={modal}
                                setupKey={key}
                                data={data}
                                onModalClose={onModalClose}
                                isCollapsible
                                isOpen={isOpen}
                                onToggle={toggleCollapse}
                                bottomMargin={{ marginBottom: 0 }}
                                style={
                                    isOpen
                                        ? {
                                              borderBottomLeftRadius: 0,
                                              borderBottomRightRadius: 0
                                          }
                                        : {}
                                }
                            />
                            {isAnalyticsScript && analyticsScriptOpen && (
                                <StyledCollapseContent>
                                    <Divider margin='0' />
                                    <SubdomainCollapse
                                        openedCollapse={SETUP_KEYS.ANALYTICS_SCRIPT}
                                        initialDomains={setupInfo.restCustomDomains || []}
                                    />
                                </StyledCollapseContent>
                            )}
                            {isPurchaseEvent && purchaseEventOpen && (
                                <StyledCollapseContent>
                                    <Divider margin='0' />
                                    <SubdomainCollapse
                                        openedCollapse={SETUP_KEYS.PURCHASE_EVENT}
                                        initialDomains={setupInfo.restCustomDomains || []}
                                    />
                                </StyledCollapseContent>
                            )}
                        </div>
                    );
                })}
            </WhiteCover>
        </Spin>
    );
});

export default IntegrationBig;
